/**
 * Helper functions for AddInventoryStepper submissions
 * Handles Add Stock, Usage, and Transfer for all inventory types
 */

import { supabase } from '../../../../../supabaseClient';

/**
 * Upload documents to Supabase storage
 * @param {string} companyId - Company ID
 * @param {string} inventoryType - Type of inventory (bottles, caps, labels, supplies)
 * @param {Object} documents - Object with document files { invoice: File, po: File, etc. }
 * @returns {Promise<Object>} Object with uploaded file paths
 */
async function uploadDocuments(companyId, inventoryType, documents) {
  const uploads = {};
  const timestamp = Date.now();
  
  const documentTypes = [
    { key: 'invoice', bucket: `${inventoryType}-transactions-invoices` },
    { key: 'po', bucket: `${inventoryType}-transactions-po` },
    { key: 'coa', bucket: `${inventoryType}-transactions-coa` },
    { key: 'msds', bucket: `${inventoryType}-transactions-msds` },
    { key: 'bol', bucket: `${inventoryType}-transactions-bol` }
  ];
  
  for (const { key, bucket } of documentTypes) {
    const file = documents[key];
    if (!file) continue;
    
    try {
      // Generate unique file path: companyId/timestamp_originalname
      const fileExt = file.name.split('.').pop();
      const fileName = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      const filePath = `${companyId}/${fileName}`;
      
      // Upload file to storage
      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });
      
      if (error) {
        console.error(`Error uploading ${key}:`, error);
        continue;
      }
      
      uploads[key] = {
        path: filePath,
        name: file.name,
        size: file.size
      };
    } catch (err) {
      console.error(`Error uploading ${key}:`, err);
    }
  }
  
  return uploads;
}

/**
 * Submit Add Stock transaction
 * Creates inventory_creation transaction for bottles/caps/labels/supplies
 */
export async function submitAddStock(companyId, inventoryType, config, data) {
  try {
    const { item, location, quantity, lotNumber, batchNumber, vendor, invoiceNumber, invoiceDate, poNumber, poDate, unitCost, documents, notes } = data;

    // Validate required fields
    if (!item || !location || !quantity || quantity <= 0) {
      throw new Error('Item, location, and positive quantity are required');
    }
    
    if (!lotNumber || !batchNumber) {
      throw new Error('Lot number and batch number are required');
    }

    // Use provided unitCost or fallback to item definition
    const finalUnitCost = unitCost || item.unit_cost || item.cost || null;

    // Prepare base transaction data
    const transactionData = {
      company_id: companyId,
      [config.itemIdField]: item.id,
      location_id: location.id,
      transaction_type: 'inventory_creation',
      quantity: parseFloat(quantity),
      lot_number: lotNumber || null,
      batch_number: batchNumber || null,
      supplier_id: vendor?.id || null,
      invoice_number: invoiceNumber || null,
      invoice_date: invoiceDate || null,
      po_number: poNumber || null,
      po_date: poDate || null,
      unit_cost: finalUnitCost ? parseFloat(finalUnitCost) : null,
      transaction_date: new Date().toISOString(),
      notes: notes || null
    };
    
    // Handle document uploads to storage buckets
    if (documents && Object.keys(documents).length > 0) {
      const documentUploads = await uploadDocuments(companyId, inventoryType, documents);
      
      // Add document paths to transaction data
      if (documentUploads.invoice) {
        transactionData.invoice_file_path = documentUploads.invoice.path;
        transactionData.invoice_file_name = documentUploads.invoice.name;
        transactionData.invoice_file_size = documentUploads.invoice.size;
      }
      if (documentUploads.po) {
        transactionData.po_file_path = documentUploads.po.path;
        transactionData.po_file_name = documentUploads.po.name;
        transactionData.po_file_size = documentUploads.po.size;
      }
      if (documentUploads.coa) {
        transactionData.coa_file_path = documentUploads.coa.path;
        transactionData.coa_file_name = documentUploads.coa.name;
        transactionData.coa_file_size = documentUploads.coa.size;
      }
      if (documentUploads.msds) {
        transactionData.msds_file_path = documentUploads.msds.path;
        transactionData.msds_file_name = documentUploads.msds.name;
        transactionData.msds_file_size = documentUploads.msds.size;
      }
      if (documentUploads.bol) {
        transactionData.bol_file_path = documentUploads.bol.path;
        transactionData.bol_file_name = documentUploads.bol.name;
        transactionData.bol_file_size = documentUploads.bol.size;
      }
    }

    // Insert transaction
    const { data: insertedTransaction, error: transactionError } = await supabase
      .from(config.transactionsTable)
      .insert([transactionData])
      .select()
      .single();

    if (transactionError) throw transactionError;

    // Delete any inactive (ghost) records with the same lot/batch to prevent old data reappearing
    await supabase
      .from(config.itemsTable)
      .delete()
      .eq('company_id', companyId)
      .eq(config.itemIdField, item.id)
      .eq('location_id', location.id)
      .eq('lot_number', lotNumber || '')
      .eq('batch_number', batchNumber || '')
      .eq('is_active', false);

    // Check for existing ACTIVE item with same lot/batch
    const { data: existingItem, error: existingError } = await supabase
      .from(config.itemsTable)
      .select('*')
      .eq('company_id', companyId)
      .eq(config.itemIdField, item.id)
      .eq('location_id', location.id)
      .eq('lot_number', lotNumber || '')
      .eq('batch_number', batchNumber || '')
      .eq('is_active', true)
      .maybeSingle();

    if (existingError && existingError.code !== 'PGRST116') {
      throw existingError;
    }

    if (existingItem) {
      // Update existing active item - add quantity, point to new transaction, clear old notes
      const { error: updateError } = await supabase
        .from(config.itemsTable)
        .update({
          current_quantity: existingItem.current_quantity + parseFloat(quantity),
          source_transaction_id: insertedTransaction.id,
          notes: notes || null,
          is_active: true,
          last_updated_at: new Date().toISOString()
        })
        .eq('id', existingItem.id);

      if (updateError) throw updateError;
    } else {
      // Create new item
      const itemData = {
        company_id: companyId,
        [config.itemIdField]: item.id,
        location_id: location.id,
        lot_number: lotNumber || '',
        batch_number: batchNumber || '',
        current_quantity: parseFloat(quantity),
        original_quantity: parseFloat(quantity),
        source_transaction_id: insertedTransaction.id,
        notes: notes || null,
        last_updated_at: new Date().toISOString(),
        is_active: true
      };

      const { error: createError } = await supabase
        .from(config.itemsTable)
        .insert([itemData]);

      if (createError) throw createError;
    }

    // Update aggregate inventory
    await updateAggregateInventory(companyId, inventoryType, config, item.id, location.id);

    return { success: true, message: 'Stock added successfully' };
  } catch (error) {
    console.error('Error adding stock:', error);
    return { success: false, message: error.message };
  }
}

/**
 * Submit Usage transaction
 * Removes inventory using FIFO
 */
export async function submitUsage(companyId, inventoryType, config, data) {
  try {
    const { item, location, quantity, notes } = data;

    // Validate required fields
    if (!item || !location || !quantity || quantity <= 0) {
      throw new Error('Item, location, and positive quantity are required');
    }

    // Check available inventory
    const { data: availableInventory, error: inventoryError } = await supabase
      .from(config.inventoryTable)
      .select('current_quantity')
      .eq('company_id', companyId)
      .eq(config.itemIdField, item.id)
      .eq('location_id', location.id)
      .maybeSingle();

    if (inventoryError && inventoryError.code !== 'PGRST116') {
      throw inventoryError;
    }

    const availableQty = availableInventory?.current_quantity || 0;
    const requestedQty = parseFloat(quantity);

    if (requestedQty > availableQty) {
      throw new Error(`Insufficient inventory. Available: ${availableQty}, Requested: ${requestedQty}`);
    }

    // Create transaction
    const transactionData = {
      company_id: companyId,
      [config.itemIdField]: item.id,
      location_id: location.id,
      transaction_type: 'usage',
      quantity: -Math.abs(requestedQty),
      notes: notes || null,
      transaction_date: new Date().toISOString()
    };

    const { error: transactionError } = await supabase
      .from(config.transactionsTable)
      .insert([transactionData]);

    if (transactionError) throw transactionError;

    // Reduce inventory using FIFO
    await reduceInventoryFIFO(companyId, config, item.id, location.id, requestedQty);

    // Update aggregate
    await updateAggregateInventory(companyId, inventoryType, config, item.id, location.id);

    return { success: true, message: 'Usage recorded successfully' };
  } catch (error) {
    console.error('Error recording usage:', error);
    return { success: false, message: error.message };
  }
}

/**
 * Submit Transfer transaction
 * Moves inventory between locations
 */
export async function submitTransfer(companyId, inventoryType, config, data) {
  try {
    const { item, fromLocation, toLocation, quantity, notes } = data;

    // Validate required fields
    if (!item || !fromLocation || !toLocation || !quantity || quantity <= 0) {
      throw new Error('Item, locations, and positive quantity are required');
    }

    if (fromLocation.id === toLocation.id) {
      throw new Error('From and To locations must be different');
    }

    // Check available inventory at source
    const { data: sourceInventory, error: inventoryError } = await supabase
      .from(config.inventoryTable)
      .select('current_quantity')
      .eq('company_id', companyId)
      .eq(config.itemIdField, item.id)
      .eq('location_id', fromLocation.id)
      .maybeSingle();

    if (inventoryError && inventoryError.code !== 'PGRST116') {
      throw inventoryError;
    }

    const availableQty = sourceInventory?.current_quantity || 0;
    const requestedQty = parseFloat(quantity);

    if (requestedQty > availableQty) {
      throw new Error(`Insufficient inventory at ${fromLocation.name}. Available: ${availableQty}, Requested: ${requestedQty}`);
    }

    // Create transfer transaction
    const transactionData = {
      company_id: companyId,
      [config.itemIdField]: item.id,
      from_location_id: fromLocation.id,
      to_location_id: toLocation.id,
      location_id: toLocation.id,
      transaction_type: 'transfer',
      quantity: requestedQty,
      notes: notes || null,
      transaction_date: new Date().toISOString()
    };

    const { error: transactionError } = await supabase
      .from(config.transactionsTable)
      .insert([transactionData]);

    if (transactionError) throw transactionError;

    // Reduce from source using FIFO
    await reduceInventoryFIFO(companyId, config, item.id, fromLocation.id, requestedQty);

    // Add to destination (using first lot/batch from source for simplicity)
    const { data: sourceItems } = await supabase
      .from(config.itemsTable)
      .select('lot_number, batch_number, unit_cost')
      .eq('company_id', companyId)
      .eq(config.itemIdField, item.id)
      .eq('location_id', fromLocation.id)
      .gt('current_quantity', 0)
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();

    const lotNumber = sourceItems?.lot_number || '';
    const batchNumber = sourceItems?.batch_number || '';
    const unitCost = sourceItems?.unit_cost || null;

    // Delete any inactive ghost records at destination with same lot/batch
    await supabase
      .from(config.itemsTable)
      .delete()
      .eq('company_id', companyId)
      .eq(config.itemIdField, item.id)
      .eq('location_id', toLocation.id)
      .eq('lot_number', lotNumber)
      .eq('batch_number', batchNumber)
      .eq('is_active', false);

    // Check if active item exists at destination
    const { data: destItem } = await supabase
      .from(config.itemsTable)
      .select('*')
      .eq('company_id', companyId)
      .eq(config.itemIdField, item.id)
      .eq('location_id', toLocation.id)
      .eq('lot_number', lotNumber)
      .eq('batch_number', batchNumber)
      .eq('is_active', true)
      .maybeSingle();

    if (destItem) {
      // Update existing active item
      const { error: updateError } = await supabase
        .from(config.itemsTable)
        .update({
          current_quantity: destItem.current_quantity + requestedQty,
          is_active: true,
          last_updated_at: new Date().toISOString()
        })
        .eq('id', destItem.id);

      if (updateError) throw updateError;
    } else {
      // Create new
      const { error: createError } = await supabase
        .from(config.itemsTable)
        .insert([{
          company_id: companyId,
          [config.itemIdField]: item.id,
          location_id: toLocation.id,
          lot_number: lotNumber,
          batch_number: batchNumber,
          current_quantity: requestedQty,
          original_quantity: requestedQty,
          source_transaction_id: null,
          last_updated_at: new Date().toISOString(),
          is_active: true
        }]);

      if (createError) throw createError;
    }

    // Update aggregates for both locations
    await updateAggregateInventory(companyId, inventoryType, config, item.id, fromLocation.id);
    await updateAggregateInventory(companyId, inventoryType, config, item.id, toLocation.id);

    return { success: true, message: 'Transfer completed successfully' };
  } catch (error) {
    console.error('Error transferring inventory:', error);
    return { success: false, message: error.message };
  }
}

/**
 * Reduce inventory using FIFO (First In, First Out)
 */
async function reduceInventoryFIFO(companyId, config, itemId, locationId, quantityToReduce) {
  let remainingQty = quantityToReduce;

  // Get items ordered by created_at (FIFO)
  const { data: items, error } = await supabase
    .from(config.itemsTable)
    .select('*')
    .eq('company_id', companyId)
    .eq(config.itemIdField, itemId)
    .eq('location_id', locationId)
    .gt('current_quantity', 0)
    .order('created_at', { ascending: true });

  if (error) throw error;

  for (const item of items) {
    if (remainingQty <= 0) break;

    const qtyToDeduct = Math.min(item.current_quantity, remainingQty);
    const newQty = item.current_quantity - qtyToDeduct;

    if (newQty > 0) {
      // Update with reduced quantity
      await supabase
        .from(config.itemsTable)
        .update({
          current_quantity: newQty,
          last_updated_at: new Date().toISOString()
        })
        .eq('id', item.id);
    } else {
      // Delete if quantity reaches 0
      await supabase
        .from(config.itemsTable)
        .delete()
        .eq('id', item.id);
    }

    remainingQty -= qtyToDeduct;
  }

  if (remainingQty > 0) {
    throw new Error(`Could not reduce full quantity. Remaining: ${remainingQty}`);
  }
}

/**
 * Update aggregate inventory table
 */
async function updateAggregateInventory(companyId, inventoryType, config, itemId, locationId) {
  // Calculate total quantity from all items
  const { data: items, error: itemsError } = await supabase
    .from(config.itemsTable)
    .select('current_quantity')
    .eq('company_id', companyId)
    .eq(config.itemIdField, itemId)
    .eq('location_id', locationId);

  if (itemsError) throw itemsError;

  const totalQuantity = items.reduce((sum, item) => sum + (item.current_quantity || 0), 0);

  // Check if aggregate exists
  const { data: aggregate } = await supabase
    .from(config.inventoryTable)
    .select('*')
    .eq('company_id', companyId)
    .eq(config.itemIdField, itemId)
    .eq('location_id', locationId)
    .maybeSingle();

  if (aggregate) {
    if (totalQuantity > 0) {
      // Update existing
      await supabase
        .from(config.inventoryTable)
        .update({
          current_quantity: totalQuantity,
          [config.lastUpdatedField || 'last_updated']: new Date().toISOString()
        })
        .eq('id', aggregate.id);
    } else {
      // Delete if quantity is 0
      await supabase
        .from(config.inventoryTable)
        .delete()
        .eq('id', aggregate.id);
    }
  } else if (totalQuantity > 0) {
    // Create new aggregate
    await supabase
      .from(config.inventoryTable)
      .insert([{
        company_id: companyId,
        [config.itemIdField]: itemId,
        location_id: locationId,
        current_quantity: totalQuantity,
        [config.lastUpdatedField || 'last_updated']: new Date().toISOString()
      }]);
  }
}
